import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

import { BasicSearchService } from '../../../services/basic-search/basic-search.service';
import {
  SearchResultProcessorService,
  SearchImage,
  DEFAULT_PROFILE_AVATAR_SRC,
} from '../../../services/search-result-processor/search-result-processor.service';
import { createPlatformIconDataUrl } from '../../../services/link-analysis/platform-icon.util';
import {
  canStartImageSearch as canStartImageSearchUtil,
  navigateToOsintImageSearch,
  isFaceDetectionReady,
  toAbsoluteOsintImageUrl,
} from '../../../services/osint-search/image-search-from-result.util';
import { UtilsService } from '../../../services/utils/utils.service';
import {
  deniesFieldSearchIcon,
  isLeftoverNotFoundSource,
} from '../../../utils/canonical-field-key';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { AbacusStructuredValueComponent } from '../../osint-search/shared/abacus-structured-value/abacus-structured-value.component';
import { GoogleCseService } from '../../../services/google-cse/google-cse.service';
import {
  GoogleOsintQueryBuilder,
  GoogleOsintQueryVariant,
} from '../../../services/google-cse/google-osint-query.builder';
import { SafeResourceUrl } from '@angular/platform-browser';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import {
  isVendorSyncComplete,
} from '../../../services/search-polling/osint-job-poll-response.util';

@Component({
  selector: 'app-basic-search-result',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, HeaderComponent, SidebarComponent, AbacusStructuredValueComponent],
  templateUrl: './basic-search-result.component.html',
  styleUrl: './basic-search-result.component.css',
})
export class BasicSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  searchResultImages: SearchImage[] = [];
  searchResultDetails: any[] = [];
  searchResultDetailsFiltered: any[] = [];
  aggregatedNames: string[] = [];
  aggregatedEmails: string[] = [];

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;
  backgroundRefreshPending: boolean = false;
  hasNoResults: boolean = false;
  loadingImages: boolean = false;
  searchTerm: string = '';
  notes: string = '';
  selectedImage: SearchImage | null = null;
  selectedImageIndex: number = -1;
  expandedBase64Strings: Set<string> = new Set();
  private processedIcons: WeakSet<HTMLImageElement> = new WeakSet();
  private pollSub?: Subscription;
  permissions: any = {};

  showGoogleResults: boolean = true;
  googleQueryVariants: GoogleOsintQueryVariant[] = [];
  selectedGoogleQueryId: string | null = null;
  googleResultsUrl: SafeResourceUrl | null = null;
  googleSearchHref: string | null = null;

  get isLoadingResults(): boolean {
    const hasResults =
      (this.searchResultDetails?.length > 0 ||
        this.searchResultImages?.length > 0) &&
      !this.loadingImages;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults
    );
  }

  get totalSourcesCount(): number {
    return this.searchResultDetails?.length || 0;
  }

  get matchCount(): number {
    return (this.searchResultDetails || []).filter(
      (p) => this.getPlatformStatus(p) === 'match',
    ).length;
  }

  get errorCount(): number {
    return (this.searchResultDetails || []).filter(
      (p) => this.getPlatformStatus(p) === 'error',
    ).length;
  }

  get noRecordCount(): number {
    return (this.searchResultDetails || []).filter(
      (p) => this.getPlatformStatus(p) === 'no-record',
    ).length;
  }

  get resolvedCount(): number {
    return this.matchCount + this.noRecordCount + this.errorCount;
  }

  get confidenceLevel(): 'high' | 'medium' | 'low' {
    const total = this.totalSourcesCount;
    if (!total) return 'low';
    const ratio = this.matchCount / total;
    if (ratio >= 0.45 || this.matchCount >= 4) return 'high';
    if (ratio >= 0.2 || this.matchCount >= 2) return 'medium';
    return 'low';
  }

  get identityRows(): Array<{ label: string; values: string[] }> {
    const rows: Array<{ label: string; values: string[] }> = [];
    const result =
      this.selectedSearchResult?.result || this.selectedSearchResult || {};

    if (this.aggregatedNames?.length) {
      rows.push({ label: 'Names', values: [...this.aggregatedNames] });
    }
    if (this.aggregatedEmails?.length) {
      rows.push({ label: 'Emails', values: [...this.aggregatedEmails] });
    }

    const phone =
      this.selectedSearchResult?.searched_phone ||
      result.number ||
      result.searched_phone;
    if (phone) {
      rows.push({ label: 'Phone', values: [String(phone)] });
    }

    const currentCarrier =
      this.formatCarrierValue(result.current_carrier) ||
      this.formatCarrierValue(this.selectedSearchResult?.current_carrier) ||
      this.extractCarrierLabel();
    if (currentCarrier) {
      rows.push({ label: 'Carrier', values: [currentCarrier] });
    }

    const originalCarrier =
      this.formatCarrierValue(result.original_carrier) ||
      this.formatCarrierValue(this.selectedSearchResult?.original_carrier);
    if (originalCarrier) {
      rows.push({ label: 'Original Carrier', values: [originalCarrier] });
    }

    const country = this.extractCountryLabel();
    if (country) {
      rows.push({ label: 'Country', values: [country] });
    }

    const tags = Array.isArray(result.tags)
      ? result.tags.filter((tag: unknown) => !this.isEmptyValue(tag)).map(String)
      : [];
    if (tags.length) {
      rows.push({ label: 'Tags', values: tags });
    }

    const statusFields: Array<[string, string]> = [
      ['Ported', 'ported'],
      ['Reachable', 'reachable'],
      ['Roaming', 'roaming'],
      ['Valid', 'valid'],
    ];
    for (const [label, key] of statusFields) {
      const value = result[key] ?? this.selectedSearchResult?.[key];
      if (this.isEmptyValue(value)) continue;
      rows.push({
        label,
        values: [
          typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value),
        ],
      });
    }

    return rows;
  }

  getPlatformStatus(platform: any): 'match' | 'no-record' | 'error' {
    if (!platform) return 'no-record';
    if (platform.error || platform.hasError || platform.status === 'error') {
      return 'error';
    }
    const blob = JSON.stringify(platform?.entries || []).toLowerCase();
    if (
      platform.emptyAccount ||
      platform.notFound ||
      blob.includes('not found') ||
      blob.includes('no account') ||
      blob.includes('account not found')
    ) {
      return 'no-record';
    }
    const hasMeaningfulDetail = (platform.entries || []).some((entry: any) =>
      this.getVisibleDetails(entry).some((detail: any) => {
        const label = String(detail?.label || '').toLowerCase();
        if (label.includes('has account') || label.includes('category')) {
          return false;
        }
        return !this.isEmptyValue(detail?.value);
      }),
    );
    return hasMeaningfulDetail ? 'match' : 'no-record';
  }

  getStatusLabel(platform: any): string {
    const status = this.getPlatformStatus(platform);
    if (status === 'match') return 'MATCH';
    if (status === 'error') return 'ERROR';
    return 'NO RECORD';
  }

  getStatusIcon(platform: any): string {
    const status = this.getPlatformStatus(platform);
    if (status === 'match') return 'fas fa-check-circle';
    if (status === 'error' || status === 'no-record') return 'fas fa-times-circle';
    return 'fas fa-exclamation-triangle';
  }

  /** Matches first, then errors, then "no record" sources; API order kept within each group. */
  private sortPlatformsByStatus(platforms: any[]): any[] {
    const rank = { match: 0, error: 1, 'no-record': 2 } as const;
    return [...platforms].sort(
      (a, b) =>
        rank[this.getPlatformStatus(a)] - rank[this.getPlatformStatus(b)],
    );
  }

  private getSourceKeysWithNoAccount(result: any): Set<string> {
    const hiddenSources = new Set<string>();
    const sources = result?.sources;

    if (!sources || typeof sources !== 'object') {
      return hiddenSources;
    }

    Object.entries(sources).forEach(([sourceKey, sourceValue]) => {
      if (
        sourceValue &&
        typeof sourceValue === 'object' &&
        ((sourceValue as any).has_account === false ||
          isLeftoverNotFoundSource(sourceValue))
      ) {
        hiddenSources.add(this.normalizePlatformNameForLookup(sourceKey));
      }
    });

    return hiddenSources;
  }

  private normalizePlatformNameForLookup(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');
  }

  getPlatformIcon(platform: any): string {
    const key = String(platform?.platform || platform?.name || '').trim();
    const generated = createPlatformIconDataUrl(key, false);
    if (generated) return generated;
    if (platform?.icon) return platform.icon;
    return createPlatformIconDataUrl('source', false);
  }

  getPlatformCategory(platform: any): string | null {
    for (const entry of platform?.entries || []) {
      for (const detail of this.getVisibleDetails(entry)) {
        const label = String(detail?.label || '').toLowerCase();
        if (label === 'category' || label === 'type') {
          const value = this.utilsService.formatDisplayValue(detail.value);
          if (value && value !== 'N/A') return value;
        }
      }
    }
    return null;
  }

  getVisibleDetails(entry: any): any[] {
    return (entry?.details || []).filter(
      (detail: any) =>
        !this.searchResultProcessor.isHiddenDisplayField(detail?.label),
    );
  }

  getVisibleEntries(platform: any): any[] {
    return (platform?.entries || []).filter(
      (entry: any) => this.getVisibleDetails(entry).length > 0,
    );
  }

  private formatCarrierValue(carrier: any): string | null {
    if (this.isEmptyValue(carrier)) return null;
    if (typeof carrier === 'string') {
      const trimmed = carrier.trim();
      return trimmed || null;
    }
    if (typeof carrier === 'object') {
      const name = carrier.name || carrier.network || carrier.operator;
      if (!this.isEmptyValue(name)) return String(name);
    }
    return null;
  }

  private extractCarrierLabel(): string | null {
    const fromResult =
      this.formatCarrierValue(this.selectedSearchResult?.current_carrier) ||
      this.formatCarrierValue(this.selectedSearchResult?.result?.current_carrier) ||
      this.formatCarrierValue(this.selectedSearchResult?.carrier) ||
      this.formatCarrierValue(this.selectedSearchResult?.result?.carrier) ||
      this.formatCarrierValue(this.selectedSearchResult?.extra_data?.carrier);
    if (fromResult) return fromResult;

    for (const platform of this.searchResultDetails || []) {
      const name = String(platform?.platform || '').toLowerCase();
      if (!name.includes('carrier') && !name.includes('operator')) continue;
      for (const entry of platform.entries || []) {
        for (const detail of entry?.details || []) {
          const label = String(detail?.label || '').toLowerCase();
          if (
            label.includes('carrier') ||
            label.includes('operator') ||
            label.includes('name') ||
            label.includes('network')
          ) {
            const value = this.utilsService.formatDisplayValue(detail.value);
            if (value && value !== 'N/A') return value;
          }
        }
      }
    }
    return null;
  }

  private extractCountryLabel(): string | null {
    const fromResult =
      this.selectedSearchResult?.country ||
      this.selectedSearchResult?.result?.country ||
      this.selectedSearchResult?.extra_data?.country;
    if (fromResult) return String(fromResult);

    const phone = String(this.selectedSearchResult?.searched_phone || '');
    if (phone.startsWith('+91')) return 'India';
    if (phone.startsWith('+1')) return 'United States';
    if (phone.startsWith('+44')) return 'United Kingdom';
    return null;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private basicSearchService: BasicSearchService,
    private searchResultProcessor: SearchResultProcessorService,
    public utilsService: UtilsService,
    private googleOsintQueryBuilder: GoogleOsintQueryBuilder,
    private googleCseService: GoogleCseService,
    private searchPollingService: SearchPollingService,
  ) {}

  ngOnInit() {
    this.searchId =
      this.route.snapshot.paramMap.get('id') ||
      history.state?.searchResult?.basic_search_id ||
      '';
    this.selectedSearchResult = history.state?.searchResult;

    if (!this.searchId) {
      this.toastr.error('Missing search id');
      this.router.navigate(['/basic-search']);
      return;
    }

    this.loadResult();
  }

  ngOnDestroy() {
    this.pollSub?.unsubscribe();
    if (this.selectedImage) {
      document.body.style.overflow = '';
    }
  }

  loadResult() {
    const hasVisibleResults =
      this.searchResultDetails?.length > 0 ||
      this.searchResultImages?.length > 0;
    if (!hasVisibleResults) {
      this.loadingCompletedSearchResult = true;
    }
    this.basicSearchService.getResult(this.searchId).subscribe({
      next: (response) => {
        const data = response?.data || {};
        this.permissions = data.permissions || {};
        const refreshPending = Boolean(data.background_refresh_pending);
        this.backgroundRefreshPending = refreshPending;
        this.selectedSearchResult = {
          ...(this.selectedSearchResult || {}),
          ...data.history,
          ...data,
          searched_phone:
            data.searched_phone || data.history?.searched_phone,
          done: data.done ?? data.history?.done,
          background_refresh_pending: refreshPending,
        };
        this.notes = data.history?.notes || '';

        const vendorComplete = isVendorSyncComplete({
          done: data.done ?? data.history?.done,
          background_refresh_pending: refreshPending,
          status: data.status,
          vendor_sync_complete: data.vendor_sync_complete,
        });

        // Early hydrate: paint when done even if vendor still refreshing.
        if (data.done || data.result || data.history?.done) {
          if (data.result || data.sources) {
            this.applyResultPayload(data);
          }
        }

        if (!vendorComplete) {
          this.loadingCompletedSearchResult = false;
          if (!hasVisibleResults && !data.result && !data.sources) {
            this.loadingPolledSearchResult = true;
          }
          this.searchPollingService.ensureBackgroundPolling(
            [
              {
                job_id: String(this.searchId),
                searchType: 'basic',
                label: String(
                  data.searched_phone ||
                    data.history?.searched_phone ||
                    this.searchId,
                ),
              },
            ],
            2500,
          );
          setTimeout(() => this.initGoogleResults(), 100);
          this.pollUntilVendorSyncComplete();
          return;
        }

        this.applyResultPayload(data);
        this.loadingCompletedSearchResult = false;
        this.loadingPolledSearchResult = false;
        this.backgroundRefreshPending = false;
        setTimeout(() => this.initGoogleResults(), 100);
      },
      error: () => {
        this.loadingCompletedSearchResult = false;
        this.toastr.error('Failed to load result');
      },
    });
  }

  pollUntilVendorSyncComplete() {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(2500)
      .pipe(
        switchMap(() => this.basicSearchService.getResult(this.searchId)),
        takeWhile((res) => {
          const data = res?.data || {};
          return !isVendorSyncComplete({
            done: data.done,
            background_refresh_pending: data.background_refresh_pending,
            status: data.status,
            vendor_sync_complete: data.vendor_sync_complete,
          });
        }, true),
      )
      .subscribe({
        next: (response) => {
          const data = response?.data || {};
          const refreshPending = Boolean(data.background_refresh_pending);
          this.backgroundRefreshPending = refreshPending;
          this.selectedSearchResult = {
            ...(this.selectedSearchResult || {}),
            ...data.history,
            ...data,
            background_refresh_pending: refreshPending,
          };

          if (data.done && (data.result || data.sources)) {
            this.applyResultPayload(data);
            this.loadingPolledSearchResult = false;
            setTimeout(() => this.initGoogleResults(), 100);
          }

          if (
            isVendorSyncComplete({
              done: data.done,
              background_refresh_pending: refreshPending,
              status: data.status,
              vendor_sync_complete: data.vendor_sync_complete,
            })
          ) {
            this.backgroundRefreshPending = false;
            this.applyResultPayload(data);
            this.loadingPolledSearchResult = false;
            setTimeout(() => this.initGoogleResults(), 100);
          }
        },
        error: () => {
          this.loadingPolledSearchResult = false;
        },
      });
  }

  applyResultPayload(data: any) {
    const result = data.result || data;
    if (!result || !result.sources) {
      this.hasNoResults = true;
      this.searchResultDetails = [];
      this.searchResultImages = [];
      return;
    }

    // Ensure image_url entries are absolute API URLs (auth fetch requires http(s))
    if (Array.isArray(result.image_url)) {
      result.image_url = result.image_url.map((url: string) =>
        toAbsoluteOsintImageUrl(url),
      );
    }

    const sourceKeysWithNoAccount = this.getSourceKeysWithNoAccount(result);
    const processed = this.searchResultProcessor.process(result);
    this.searchResultDetails = this.sortPlatformsByStatus(
      (processed.platforms || []).map((platform: any) => ({
        ...platform,
        notFound: sourceKeysWithNoAccount.has(
          this.normalizePlatformNameForLookup(platform?.platformKey || platform?.platform),
        ),
      })),
    );
    this.searchResultDetailsFiltered = [...this.searchResultDetails];
    this.aggregatedNames = Array.isArray(result.name) ? result.name : [];
    this.aggregatedEmails = Array.isArray(result.email) ? result.email : [];

    // Same as OSINT phone result: auth-fetch /api/osint/image/* into blob URLs
    const imagePayload = {
      data_list: [result],
      faceDetected: result.faceDetected || result.extra_data?.faceDetected,
      face_detection_pending: Boolean(
        result.face_detection_pending ||
          result.extra_data?.face_detection_pending,
      ),
      face_detection_ready: Boolean(
        result.face_detection_ready || result.extra_data?.face_detection_ready,
      ),
    };
    this.loadingImages = true;
    this.searchResultProcessor.extractImagesFromSources(imagePayload).subscribe({
      next: (images) => {
        this.searchResultImages = images || [];
        this.loadingImages = false;
        this.hasNoResults =
          !this.searchResultDetails.length && !this.searchResultImages.length;
      },
      error: () => {
        // Fallback to raw process() images if auth fetch fails
        this.searchResultImages = processed.images || [];
        this.loadingImages = false;
        this.hasNoResults =
          !this.searchResultDetails.length && !this.searchResultImages.length;
      },
    });

    this.hasNoResults =
      !this.searchResultDetails.length && !this.searchResultImages.length;

    if (
      this.permissions?.face_search_permission &&
      (result.image_url?.length || this.searchResultImages.length) &&
      !isFaceDetectionReady(result) &&
      !isFaceDetectionReady(imagePayload)
    ) {
      setTimeout(() => this.loadResult(), 3000);
    }
  }

  /** @deprecated Use shared toAbsoluteOsintImageUrl from image-search-from-result.util */
  private toAbsoluteOsintImageUrl(url: string): string {
    return toAbsoluteOsintImageUrl(url);
  }

  onBackButtonClick() {
    this.router.navigate(['/basic-search']);
  }

  onIconError(event: Event) {
    const imgElement = event.target as HTMLImageElement | null;
    if (!imgElement) return;
    if (this.processedIcons.has(imgElement)) {
      return;
    }
    this.processedIcons.add(imgElement);
    const platformName =
      imgElement.getAttribute('data-platform') ||
      imgElement.getAttribute('alt') ||
      'source';
    const fallback = createPlatformIconDataUrl(platformName, false);
    if (fallback && imgElement.src !== fallback) {
      imgElement.src = fallback;
      return;
    }
    imgElement.src = createPlatformIconDataUrl('source', false);
  }

  onEvidenceImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement | null;
    if (!imgElement) return;
    if (imgElement.dataset['placeholder'] === '1') return;
    imgElement.dataset['placeholder'] = '1';
    imgElement.src = DEFAULT_PROFILE_AVATAR_SRC;
    imgElement.classList.add('is-placeholder');
  }

  getEvidenceImageSrc(image: SearchImage | null | undefined): string {
    const src = image?.src?.trim();
    return src ? src : DEFAULT_PROFILE_AVATAR_SRC;
  }

  openLightbox(index: number = 0) {
    if (!this.searchResultImages?.length) return;
    const safeIndex = Math.max(0, Math.min(index, this.searchResultImages.length - 1));
    this.selectedImageIndex = safeIndex;
    this.selectedImage = this.searchResultImages[safeIndex];
    document.body.style.overflow = 'hidden';
  }

  closeImageModal(): void {
    this.selectedImage = null;
    this.selectedImageIndex = -1;
    document.body.style.overflow = '';
  }

  get hasMultipleEvidenceImages(): boolean {
    return (this.searchResultImages?.length || 0) > 1;
  }

  showPreviousImage(event?: Event): void {
    event?.stopPropagation();
    if (!this.hasMultipleEvidenceImages || this.selectedImageIndex < 0) return;
    const nextIndex =
      (this.selectedImageIndex - 1 + this.searchResultImages.length) %
      this.searchResultImages.length;
    this.openLightbox(nextIndex);
  }

  showNextImage(event?: Event): void {
    event?.stopPropagation();
    if (!this.hasMultipleEvidenceImages || this.selectedImageIndex < 0) return;
    const nextIndex =
      (this.selectedImageIndex + 1) % this.searchResultImages.length;
    this.openLightbox(nextIndex);
  }

  @HostListener('document:keydown', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (!this.selectedImage) return;
    if (event.key === 'Escape') {
      this.closeImageModal();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPreviousImage();
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNextImage();
    }
  }

  canStartImageSearch(image?: any): boolean {
    if (!this.permissions?.face_search_permission) return false;
    return canStartImageSearchUtil(image);
  }

  async onImageSearch(image: any) {
    if (!this.canStartImageSearch(image)) return;
    const src =
      typeof image === 'string'
        ? image
        : image?.detectUrl || image?.src || '';
    if (!src) return;
    await navigateToOsintImageSearch(this.router, src);
  }

  onSearchTermChange() {
    if (!this.searchTerm?.trim()) {
      this.searchResultDetailsFiltered = [...this.searchResultDetails];
      return;
    }
    const term = this.searchTerm.toLowerCase().trim();
    this.searchResultDetailsFiltered = this.searchResultDetails
      .map((platform) => {
        const matchingEntries = (platform.entries || []).filter((entry: any) =>
          JSON.stringify(entry).toLowerCase().includes(term),
        );
        if (
          platform.platform?.toLowerCase().includes(term) ||
          matchingEntries.length
        ) {
          return matchingEntries.length
            ? { ...platform, entries: matchingEntries }
            : platform;
        }
        return null;
      })
      .filter(Boolean);
  }

  onExportOptionClick(_format: string) {
    this.toastr.info('Export coming soon');
  }

  onUpdateNotesButtonClick(): void {
    if (!this.notes?.trim()) {
      this.toastr.warning('Please enter notes');
      return;
    }
    this.basicSearchService.updateNotes(this.searchId, this.notes).subscribe({
      next: () => this.toastr.success('Notes updated'),
      error: () => this.toastr.error('Failed to update notes'),
    });
  }

  hasOsintSearchType(detail: any): boolean {
    return Boolean(detail?.['osint-search-type']);
  }

  shouldShowSearchIcon(detail: any): boolean {
    if (deniesFieldSearchIcon(detail)) return false;
    if (!this.permissions?.osint_search_permission) return false;
    if (!this.hasOsintSearchType(detail) || !detail?.value) return false;
    const t = String(detail['osint-search-type']).toLowerCase();
    return ['email', 'username', 'name', 'phone'].includes(t);
  }

  getBase64Key(p: number, e: number, d: number): string {
    return `${p}-${e}-${d}`;
  }
  isBase64Expanded(key: string): boolean {
    return this.expandedBase64Strings.has(key);
  }
  toggleBase64Expansion(key: string): void {
    if (this.expandedBase64Strings.has(key)) {
      this.expandedBase64Strings.delete(key);
    } else {
      this.expandedBase64Strings.add(key);
    }
  }
  getTruncatedBase64(value: string, maxLength: number = 150): string {
    if (!value || value.length <= maxLength) return value;
    return value.substring(0, maxLength) + '...';
  }
  formatListValue(value: any[]): string {
    return Array.isArray(value)
      ? this.utilsService.joinDisplayListItems(
          value.map((item) => this.utilsService.formatListItemForDisplay(item)),
        )
      : '';
  }

  getDisplayListItems(detail: any): string[] {
    const values = Array.isArray(detail?.value) ? detail.value : [];
    const label = String(detail?.label || '').trim().toLowerCase();

    return values
      .map((item: any) => this.utilsService.formatListItemForDisplay(item, label))
      .filter((item: string) => item.length > 0);
  }
  formatObjectValue(value: any): string {
    return this.utilsService.formatKeyValueObject(value);
  }
  formatDateValue(value: any): string {
    return this.utilsService?.formatDateTime?.(value) || String(value || '');
  }
  isEmptyValue(value: any): boolean {
    if (value == null) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    if (typeof value === 'string') return !value.trim();
    return false;
  }
  getObjectKeys(obj: any): string[] {
    return obj && typeof obj === 'object' ? Object.keys(obj) : [];
  }
  formatDictKey(key: string): string {
    return key?.replace(/([A-Z])/g, ' $1').trim() || key;
  }

  jumpToGoogleResults(): void {
    this.scrollToResultSection('google-results');
  }

  scrollToResultSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Same Google CSE embed as OSINT phone search. */
  initGoogleResults(): void {
    const value =
      this.selectedSearchResult?.searched_phone ||
      this.selectedSearchResult?.number;
    if (!value) {
      this.clearGoogleResults();
      return;
    }

    const variants = this.googleOsintQueryBuilder.buildVariants('phone', value);
    this.googleQueryVariants = variants;
    if (!variants.length) {
      this.clearGoogleResults();
      return;
    }

    this.applyGoogleQuery(variants[0], {
      type: 'phone',
      value,
      queries: variants.map((variant) => variant.query),
    });
  }

  selectGoogleQuery(variant: GoogleOsintQueryVariant): void {
    if (!variant?.query || this.selectedGoogleQueryId === variant.id) {
      return;
    }
    this.applyGoogleQuery(variant);
  }

  private applyGoogleQuery(
    variant: GoogleOsintQueryVariant,
    context?: { type?: string; value?: string; queries?: string[] },
  ): void {
    this.selectedGoogleQueryId = variant.id;
    const view = this.googleCseService.getEmbeddedSearch(variant.query, context);
    this.googleResultsUrl = view?.embedUrl ?? null;
    this.googleSearchHref = view?.href ?? null;
  }

  private clearGoogleResults(): void {
    this.googleQueryVariants = [];
    this.selectedGoogleQueryId = null;
    this.googleResultsUrl = null;
    this.googleSearchHref = null;
  }
}
